import { Component, Input, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Visita, VisitasService, EstadoVisita } from '../../services/visitas.service';
import { COLORES_ESTADOS } from '../../config/colores-visitas.config';
import * as L from 'leaflet';

// Fix para íconos de marcador
delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
  iconUrl: 'assets/leaflet/marker-icon.png',
  shadowUrl: 'assets/leaflet/marker-shadow.png',
});

/**
 * Componente que muestra un mapa con las ubicaciones de las visitas.
 * Utiliza OpenStreetMap con Leaflet para renderizar el mapa.
 * 
 * Puede recibir las visitas de dos formas:
 * 1. Mediante el @Input visitas - para sincronizar con otro componente (ej: tarjeta-visitas)
 * 2. Si no recibe visitas, muestra un mensaje indicando que no hay datos
 */
@Component({
  selector: 'app-mapa-ubicacion-visitas',
  templateUrl: './mapa-ubicacion-visitas.component.html',
  styleUrls: ['./mapa-ubicacion-visitas.component.scss'],
  standalone: false
})
export class MapaUbicacionVisitasComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private static contadorId = 0;

  /** Título del componente */
  @Input() titulo: string = 'Ubicación de Visitas';

  /** Alto del mapa en píxeles */
  @Input() alturaMap: string = '400px';

  /** 
   * Lista de visitas a mostrar en el mapa.
   * Si se proporciona, el mapa mostrará estas visitas.
   * Si cambia, el mapa se actualizará automáticamente.
   */
  @Input() visitas: Visita[] = [];

  /** Indica si los datos están cargando (controlado externamente) */
  @Input() cargando: boolean = false;

  /** Texto de información que se mostrará en un popover */
  @Input() informacion: string = '';

  /** Identificador único para el popover de información */
  public idUnico = `informacion-mapa-ubicacion-${MapaUbicacionVisitasComponent.contadorId++}`;

  @ViewChild('mapaContainer', { static: false }) mapaContainer!: ElementRef;

  private mapa!: any;
  private layerMarcadores!: any;
  private mapaInicializado = false;
  private inicializando = false;
  private resizeObserver: ResizeObserver | null = null;
  private boundsMarcadores: any = null;

  // Centro de Chile por defecto (Santiago)
  private centroChile: any = [-33.4489, -70.6693];
  private zoomInicial = 5;

  /** Estados de visita cargados desde la API */
  estadosVisita: EstadoVisita[] = [];
  cargandoEstados: boolean = false;

  private readonly visitasService = inject(VisitasService);

  

  /**
   * Indica si hay al menos una visita con coordenadas numéricas válidas.
   */
  get tieneVisitasConCoordenadas(): boolean {
    if (!this.visitas || this.visitas.length === 0) return false;
    return this.visitas.some(v => this.latLngDeVisita(v) !== null);
  }

  /** Lat/lng desde campos planos o productor.coordenadas (acepta string numérico). */
  private latLngDeVisita(visita: Visita): [number, number] | null {
    const desdeCampos = this.parseCoord(visita.latitud, visita.longitud);
    if (desdeCampos) return desdeCampos;
    const coordenadas = visita.productor?.coordenadas;
    if (coordenadas) return this.parseCoord(coordenadas.lat, coordenadas.lng);
    return null;
  }

  private parseCoord(lat: unknown, lng: unknown): [number, number] | null {
    if (lat === null || lat === undefined || lat === '' ||
        lng === null || lng === undefined || lng === '') {
      return null;
    }
    const latN = Number(lat);
    const lngN = Number(lng);
    if (!isFinite(latN) || !isFinite(lngN)) return null;
    return [latN, lngN];
  }

  ngOnInit(): void {
    // Cargar estados de visita desde la API
    this.cargarEstadosVisita();
  }

  /**
   * Carga los estados de visita disponibles desde la API
   */
  private cargarEstadosVisita(): void {
    this.cargandoEstados = true;
    this.visitasService.obtenerEstadosVisita().subscribe({
      next: (estados) => {
        this.estadosVisita = estados;
        this.cargandoEstados = false;
      },
      error: (error) => {
        console.error('Error al cargar estados de visita:', error);
        this.estadosVisita = [];
        this.cargandoEstados = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.programarInicializacionMapa();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visitas'] && !changes['cargando']) {
      return;
    }

    if (this.tieneVisitasConCoordenadas) {
      if (this.mapaInicializado && this.layerMarcadores) {
        if (changes['visitas']) {
          this.actualizarMarcadores();
        }
      } else {
        this.programarInicializacionMapa();
      }
    } else {
      this.destruirMapa();
    }
  }

  ngOnDestroy(): void {
    this.destruirMapa();
  }

  private programarInicializacionMapa(): void {
    setTimeout(() => {
      if (!this.tieneVisitasConCoordenadas || this.mapaInicializado) {
        return;
      }
      void this.inicializarMapa()
        .then(() => {
          if (this.visitas?.length) {
            this.actualizarMarcadores();
          }
        })
        .catch(console.error);
    }, 0);
  }

  private destruirMapa(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.inicializando = false;
    if (this.mapa) {
      this.layerMarcadores?.clearLayers();
      this.mapa.remove();
      this.mapa = null;
    }
    this.mapaInicializado = false;
    this.boundsMarcadores = null;
  }

  /**
   * Observa cambios de tamaño del contenedor del mapa para re-renderizar
   * (p. ej. al expandir/colapsar el menú lateral).
   */
  private observarCambioTamanoContenedor(): void {
    if (!this.mapaContainer?.nativeElement || !this.mapa) return;
    this.resizeObserver?.disconnect();
    let ultimoAncho = 0;
    let ultimoAlto = 0;
    this.resizeObserver = new ResizeObserver(() => {
      const el = this.mapaContainer?.nativeElement as HTMLElement | undefined;
      if (!this.mapa || !el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (Math.abs(w - ultimoAncho) < 2 && Math.abs(h - ultimoAlto) < 2) return;
      ultimoAncho = w;
      ultimoAlto = h;
      setTimeout(() => this.ajustarVistaMapa(), 50);
    });
    this.resizeObserver.observe(this.mapaContainer.nativeElement);
  }

  /**
   * Inicializa el mapa de Leaflet con OpenStreetMap
   */
  private async inicializarMapa(): Promise<void> {
    if (this.mapaInicializado || this.inicializando) {
      return;
    }
    this.inicializando = true;

    try {
      for (let i = 0; i < 30; i++) {
        const candidato = this.mapaContainer?.nativeElement as HTMLElement | undefined;
        if (candidato && candidato.clientWidth > 0 && candidato.clientHeight > 0) {
          break;
        }
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }

      const el = this.mapaContainer?.nativeElement as HTMLElement | undefined;
      if (!el || this.mapaInicializado) {
        return;
      }

      const capaOSM = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      });

      const capaSat = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics',
          maxZoom: 19
        }
      );

      this.mapa = L.map(el, {
        center: this.centroChile,
        zoom: this.zoomInicial,
        zoomControl: false,
        attributionControl: true,
        layers: [capaOSM]
      });

      L.control.zoom({ position: 'bottomleft' }).addTo(this.mapa);
      L.control.layers(
        { 'Mapa': capaOSM, 'Satélite': capaSat },
        {}
      ).addTo(this.mapa);

      this.layerMarcadores = L.layerGroup().addTo(this.mapa);
      this.mapaInicializado = true;
      this.observarCambioTamanoContenedor();
      setTimeout(() => this.ajustarVistaMapa(), 50);
    } finally {
      this.inicializando = false;
    }
  }


  /**
   * Actualiza los marcadores en el mapa basándose en las visitas
   * Filtra las visitas para mostrar solo un marcador por productor único
   */
  private actualizarMarcadores(): void {
    if (!this.mapa || !this.layerMarcadores) {
      return;
    }

    this.layerMarcadores.clearLayers();
    this.boundsMarcadores = null;

    if (!this.visitas || this.visitas.length === 0) {
      return;
    }

    const productoresUnicos = new Map<string, Visita>();

    this.visitas.forEach((visita) => {
      if (!this.latLngDeVisita(visita)) {
        return;
      }

      const clave = visita.productor?.id_productor || visita.id_visita;
      if (!clave) {
        return;
      }

      if (!productoresUnicos.has(clave)) {
        productoresUnicos.set(clave, visita);
      }
    });

    const bounds = L.latLngBounds([]);
    let visitasConCoordenadas = 0;

    productoresUnicos.forEach((visita) => {
      const posicion = this.latLngDeVisita(visita);
      if (!posicion) {
        return;
      }

      const colorEstado = this.getColorEstado(visita.estado);

      const marcador = L.circleMarker(posicion, {
        radius: 12,
        fillColor: colorEstado,
        color: '#000',
        weight: 3,
        opacity: 1,
        fillOpacity: 1
      }).bindPopup(this.crearContenidoPopup(visita));

      this.layerMarcadores.addLayer(marcador);
      bounds.extend(posicion);
      visitasConCoordenadas++;
    });

    if (visitasConCoordenadas > 0 && bounds.isValid()) {
      this.boundsMarcadores = bounds;
    }
    this.ajustarVistaMapa();
  }

  /** Recalcula tamaño del contenedor y vuelve a encuadrar los marcadores. */
  private ajustarVistaMapa(): void {
    if (!this.mapa) return;
    this.mapa.invalidateSize({ animate: false });
    if (this.boundsMarcadores?.isValid()) {
      this.mapa.fitBounds(this.boundsMarcadores, { padding: [50, 50], maxZoom: 10, animate: false });
    }
  }

  /**
   * Crea el contenido HTML del popup para una visita
   */
  private crearContenidoPopup(visita: Visita): string {
    const fechaFormateada = this.formatearFechaVisita(visita.fecha);
    const posicion = this.latLngDeVisita(visita);
    const urlNavegacion = posicion
      ? this.generarUrlNavegacion(posicion[0], posicion[1])
      : '#';

    return `
      <div class="popup-visita">
        <h4 style="margin: 0 0 6px 0; font-size: 13px; color: #333; line-height: 1.3; word-wrap: break-word;">${visita.productor?.nombre || visita.usuario || '-'}</h4>
        <p style="margin: 3px 0; font-size: 11px; color: #666; line-height: 1.4; word-wrap: break-word;">
          <strong>Lugar:</strong> ${visita.lugar || '-'}
        </p>
        <p style="margin: 3px 0; font-size: 11px; color: #666; line-height: 1.4; word-wrap: break-word;">
          <strong>Fecha:</strong> ${fechaFormateada}
        </p>
        <p style="margin: 3px 0; font-size: 11px; color: #666; line-height: 1.4; word-wrap: break-word;">
          <strong>Unidad:</strong> ${visita.productor?.jerarquia?.descripcion || '-'}
        </p>
        <span style="display: inline-block; padding: 2px 6px; border-radius: 10px; font-size: 10px; background-color: ${this.getColorEstado(visita.estado)}; color: white; margin-bottom: 8px;">
          ${visita.estado}
        </span>
        <div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #eee;">
          <a href="${urlNavegacion}" 
             target="_blank" 
             rel="noopener noreferrer"
             style="display: flex; align-items: center; justify-content: center; gap: 4px; padding: 6px 10px; background-color: #3880ff; color: white; text-decoration: none; border-radius: 5px; font-size: 11px; font-weight: 500;">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
            </svg>
            Ir a esta ubicación
          </a>
        </div>
      </div>
    `;
  }

  /**
   * Genera la URL para navegación hacia las coordenadas.
   * Usa el esquema geo: que funciona en móviles (abre la app de mapas nativa)
   * y Google Maps como fallback para navegadores de escritorio.
   * @param latitud Latitud del destino
   * @param longitud Longitud del destino
   * @returns URL para navegación
   */
  private generarUrlNavegacion(latitud: number, longitud: number): string {
    // Detectar si es un dispositivo móvil
    const esMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (esMobile) {
      // En móviles, usar el esquema geo: que abre la app de mapas nativa
      // El parámetro q= permite buscar la ubicación y mostrar opciones de navegación
      // Google Maps en Android y Apple Maps en iOS lo interpretan correctamente
      return `https://www.google.com/maps/dir/?api=1&destination=${latitud},${longitud}&travelmode=driving`;
    } else {
      // En escritorio, abrir Google Maps con la ruta desde la ubicación actual
      return `https://www.google.com/maps/dir/?api=1&destination=${latitud},${longitud}&travelmode=driving`;
    }
  }

  /**
   * Obtiene el color de fondo para el estado
   * Usa la configuración centralizada de colores
   */
  private getColorEstado(estado: string): string {
    const estadoLower = estado?.toLowerCase() || '';
    
    // Mapear según las descripciones del endpoint /api/v1/visita/obtenerEstadosVisita
    switch (estadoLower) {
      case 'agendada':
        return COLORES_ESTADOS.agendada.hex;
      case 'planificada':
        return COLORES_ESTADOS.planificada.hex;
      case 'finalizada':
      case 'completada':
        return COLORES_ESTADOS.finalizada.hex;
      case 'cancelada':
        return COLORES_ESTADOS.cancelada.hex;
      case 'reagendada':
        return COLORES_ESTADOS.reagendada.hex;
      default:
        return COLORES_ESTADOS.default.hex;
    }
  }

  /**
   * Obtiene la clase CSS para el estado
   * Normaliza el nombre del estado para usarlo como clase CSS
   */
  getClaseEstado(estado: string): string {
    if (!estado) return 'estado-default';
    
    // Normalizar el estado: convertir a minúsculas y reemplazar espacios por guiones
    const estadoNormalizado = estado.toLowerCase().replace(/\s+/g, '-');
    return `estado-${estadoNormalizado}`;
  }

  /**
   * Formatea una fecha ISO a formato legible
   */
  private formatearFechaVisita(fechaISO: string): string {
    if (!fechaISO) return '-';
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Refresca el tamaño del mapa (útil cuando cambia el contenedor)
   */
  refrescarMapa(): void {
    if (!this.mapa) return;
    setTimeout(() => this.ajustarVistaMapa(), 50);
  }
}
